import { Injectable } from '@angular/core';
import { Auth, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, signOut, updateProfile, updateEmail, confirmPasswordReset, setPersistence, Persistence, User, onAuthStateChanged } from '@angular/fire/auth';
import { Router } from '@angular/router';
import { inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TwoFactorService } from '../two-factor.service';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  public authState: User
  authState$ = new BehaviorSubject(null)
  private auth = inject(Auth); // Injecting AngularFire Auth
  private router = inject(Router); // Injecting Router
  private twoFactorService = inject(TwoFactorService);

  constructor() {
    // Subscribing to the AuthState from the AngularFire Auth service
    onAuthStateChanged(this.auth, (user: User) => {
      this.authState = user;
      this.authState$.next(user)
    });
  }

  /**
   * Creates a new user based on email and password.
   * @param email
   * @param password
   * @param options
   */
  public async createUser(email: string, password: string, options?: any): Promise<void> {
    const response = await createUserWithEmailAndPassword(this.auth, email, password);
    // Add logic to use `options` here if necessary
    return response.user ? Promise.resolve() : Promise.reject();
  }

  /**
   * Signs a user in through email and password.
   * @param email
   * @param password
   * @param persistence
   */
  public async signInUser(email: string, password: string, persistence?: Persistence): Promise<any> {
    // await setPersistence(this.auth, persistence)
    const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
    
    // Check 2FA status after successful login
    const uid = userCredential.user.uid;
    const twoFactorEnabled = await this.twoFactorService.isTwoFactorEnabled(uid);
    
    return {
      user: userCredential.user,
      requires2FA: twoFactorEnabled,
      requiresSetup: !twoFactorEnabled,
      uid: uid
    };
  }

  /**
   * Checks if 2FA is required for the current user
   */
  public async checkTwoFactorRequired(uid: string): Promise<boolean> {
    return await this.twoFactorService.isTwoFactorEnabled(uid);
  }

  /**
   * Checks if 2FA is enabled for a user
   */
  public async checkTwoFactorEnabled(uid: string): Promise<boolean> {
    return await this.twoFactorService.isTwoFactorEnabled(uid);
  }

  /**
   * Completes 2FA authentication by verifying TOTP code
   */
  public async completeTwoFactorAuth(uid: string, otp: string): Promise<{ success: boolean; error?: string }> {
    return await this.twoFactorService.verifyUserTOTP(uid, otp);
  }

  /**
   * Enables 2FA for a user after setup
   */
  public async enableTwoFactorForUser(uid: string, secret: string): Promise<void> {
    return await this.twoFactorService.enableTwoFactor(uid, secret);
  }

  /**
   * Signs the user out and redirects to the login page.
   */
  public async signOutUser(): Promise<void> {
    // Clear 2FA session verification before signing out
    const currentUser = this.auth.currentUser;
    if (currentUser) {
      this.twoFactorService.clearSessionVerification(currentUser.uid);
    }
    await signOut(this.auth);
    await this.router.navigate(['/login']);
  }

  /**
   * Sends a password reset email.
   * @param email
   */
  public resetPassword(email: string): Promise<void> {
    return sendPasswordResetEmail(this.auth, email);
  }

  /**
   * Confirms password reset with code and new password.
   * @param code
   * @param password
   */
  public changePassword(code: string, password: string): Promise<void> {
    return confirmPasswordReset(this.auth, code, password);
  }

  /**
   * Updates the user profile with provided data.
   * @param data
   */
  public async updateUser(data: { displayName?: string; photoURL?: string; email?: string }): Promise<void> {
    const user = this.auth.currentUser;
    if (user) {
      if (data.displayName || data.photoURL) {
        await updateProfile(user, { displayName: data.displayName, photoURL: data.photoURL });
      }
      if (data.email) {
        await updateEmail(user, data.email);
      }
    }
  }

  /**
   * Getter for the authenticated state.
   */
  get authenticated(): boolean {
    return this.authState !== null;
  }

  /**
   * Observable for the current authenticated user.
   */
  get currentUserObservable() {
    return this.auth.onAuthStateChanged;
  }

  /**
   * Getter for the current authenticated user.
   */
  get currentUser() {
    return this.auth.currentUser;
  }
}
